<template>
  <div
    class="tw-mb-1"
    data-walkthrough-id="recently-used-queries"
    data-testid="recently-used-queries"
  >
    <div
      class="tw-pt-4 tw-px-4 sf-below-1"
      :class="
        inFloatingSidepane ? 'tw-text-sm tw-font-medium' : 'sf-title sf-font-l'
      "
    >
      {{ I18n.t('companies_overview.segments_new.recently_used') }}
    </div>

    <SegmentList
      :segments="recentSegments"
      :loading="loading"
      :test-id="'recent-queries-list'"
      @item-click="selectSegment"
    >
      <template #static-item>
        <SegmentListItem
          :segment="allClientsFakeSegment"
          :loading="loading"
          @item-click="selectAllClients"
        >
          <template #tooltip>
            <i
              v-tooltip.placementBottom="allClientsTooltip"
              class="fa fa-info-circle tw-pl-1 tw-text-sf-slate-light-2"
            ></i>
          </template>
        </SegmentListItem>
      </template>
    </SegmentList>

    <div
      v-if="recommendedSegments.length > 0"
      class="tw-px-4 tw-pt-4 sf-below-1"
      :class="
        inFloatingSidepane ? 'tw-text-sm tw-font-medium' : 'sf-title sf-font-l'
      "
      data-walkthrough-id="recommended-segments"
    >
      {{ I18n.t('companies_overview.segments_new.recommended') }}
    </div>
    <SegmentList
      :segments="recommendedSegments"
      :test-id="'recommended-queries-list'"
      :loading="loading"
      @item-click="selectSegment"
    ></SegmentList>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import _ from 'lodash'
import { storeToRefs } from 'pinia'
import useI18n from '@app/shared/vue/composables/use-I18n'
import { useCompaniesStore } from '@app/insights/store/companies'
import { useSegmentsStore } from '@app/insights/store/segments'
import { useInsightsRootStore } from '@app/insights/store/insights-root'
import { useSharedSegment } from '@app/insights/composables/use-shared-segment'
import { useInsightsLayoutContext } from '@app/insights/composables/use-insights-layout-context'
import type { Segment } from '../../types/index'
import SegmentListItem from './segment-list-item.vue'
import SegmentList from './segment-list.vue'

const I18n = useI18n()
const segmentsStore = useSegmentsStore()
const { segments, segment } = storeToRefs(segmentsStore)
const { isLoadingResults: loading } = storeToRefs(useCompaniesStore())
const { recentSegments: recentSegmentsIds } = storeToRefs(
  useInsightsRootStore()
)
const { allClientsFakeSegment, selectAllClients } = useSharedSegment()
const { inFloatingSidepane } = useInsightsLayoutContext()

const allClientsTooltip = computed(() =>
  I18n.t('companies_overview.segments_new.show_all_files')
)

const recentSegments = computed(() => {
  const recent = recentSegmentsIds.value
    .map((id) =>
      segments.value.find(
        (candidate) => candidate.id === parseInt(String(id), 10)
      )
    )
    .filter(
      (candidate): candidate is Segment =>
        Boolean(candidate) && !candidate?.partner_segment_id
    )

  return _.uniq(recent).slice(0, 5)
})

const recommendedSegments = computed(() =>
  segments.value.filter((candidate) => candidate.recommended)
)

const selectSegment = (clicked: Segment): void => {
  if (!segment.value || segment.value.id !== clicked.id) {
    segmentsStore.loadSegment(clicked)
  }
}
</script>

<style lang="scss" scoped>
.sf-title {
  color: theme('colors.sf-slate.dark-2');
  margin-bottom: 4px;
}
</style>
